import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Field } from '@promtver/ui-kit';
import { ExcursionStatus, ProductionCategory } from '@promtver/contracts';

import {
  archivePartnerExcursion,
  createPartnerExcursion,
  getPartnerExcursions,
  getPartnerSites,
  updatePartnerExcursion,
} from '../../api/partner';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { categoryLabel, productionCategories } from '../../lib/format';
import type { ExcursionInput } from '../../api/types';

interface ProgramForm {
  id: string | null;
  title: string;
  description: string;
  durationMin: string;
  price: string;
  category: ProductionCategory;
  minAge: string;
  isSchool: boolean;
  siteIds: string[];
}

const EMPTY_FORM: ProgramForm = {
  id: null,
  title: '',
  description: '',
  durationMin: '60',
  price: '0',
  category: ProductionCategory.MachineBuilding,
  minAge: '',
  isSchool: false,
  siteIds: [],
};

function excursionStatusLabel(status: ExcursionStatus | string): string {
  switch (status) {
    case ExcursionStatus.Published:
      return ru.programs.statusPublished;
    case ExcursionStatus.Archived:
      return ru.programs.statusArchived;
    default:
      return ru.programs.statusDraft;
  }
}

/**
 * Управление программами (FR-MGR-05): создание, редактирование, архивирование,
 * привязка к точкам показа, назначение категории и цены.
 */
export function ProgramsPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ProgramForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const excursionsQuery = useQuery({
    queryKey: ['partner', 'excursions'],
    queryFn: getPartnerExcursions,
  });

  const sitesQuery = useQuery({
    queryKey: ['partner', 'sites'],
    queryFn: getPartnerSites,
  });

  useEffect(() => {
    setFormError(null);
  }, [form.id]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['partner', 'excursions'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: ExcursionInput = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        durationMin: Number(form.durationMin) || 0,
        // Цена хранится в копейках (ТЗ §7.3).
        price: Math.round((Number(form.price) || 0) * 100),
        minAge: form.minAge.trim() ? Number(form.minAge) : null,
        isSchool: form.isSchool,
        siteIds: form.siteIds,
      };
      if (form.id) {
        return updatePartnerExcursion(form.id, payload);
      }
      return createPartnerExcursion(payload);
    },
    onSuccess: async () => {
      setForm(EMPTY_FORM);
      setFormError(null);
      await invalidate();
    },
    onError: (error) => setFormError(errorMessage(error, ru.common.unknownError)),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archivePartnerExcursion(id),
    onSuccess: invalidate,
  });

  const excursions = excursionsQuery.data ?? [];
  const sites = sitesQuery.data ?? [];

  const openEdit = (id: string) => {
    const excursion = excursions.find((item) => item.id === id);
    if (!excursion) return;
    setForm({
      id: excursion.id,
      title: excursion.title,
      description: excursion.description,
      durationMin: String(excursion.durationMin ?? 0),
      price: String((excursion.price ?? 0) / 100),
      category: excursion.category,
      minAge: excursion.minAge ? String(excursion.minAge) : '',
      isSchool: Boolean(excursion.isSchool),
      siteIds: [],
    });
  };

  return (
    <AppShell title={ru.programs.title} subtitle={ru.programs.subtitle} active="programs">
      <Card
        title={form.id ? ru.programs.editProgram : ru.programs.newProgram}
        action={
          form.id ? (
            <Button variant="outline" onClick={() => setForm(EMPTY_FORM)}>
              {ru.common.cancel}
            </Button>
          ) : undefined
        }
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            saveMutation.mutate();
          }}
        >
          <Field
            label={ru.programs.titleField}
            value={form.title}
            onChange={(value) => setForm((previous) => ({ ...previous, title: value }))}
            required
          />
          <Field
            label={ru.programs.descriptionField}
            value={form.description}
            onChange={(value) => setForm((previous) => ({ ...previous, description: value }))}
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={ru.programs.durationField}
              value={form.durationMin}
              onChange={(value) => setForm((previous) => ({ ...previous, durationMin: value }))}
              inputMode="numeric"
              required
            />
            <Field
              label={ru.programs.priceField}
              value={form.price}
              onChange={(value) => setForm((previous) => ({ ...previous, price: value }))}
              inputMode="decimal"
              required
            />
            <Field
              label={ru.programs.minAgeField}
              value={form.minAge}
              onChange={(value) => setForm((previous) => ({ ...previous, minAge: value }))}
              inputMode="numeric"
            />
            <Select
              label={ru.programs.categoryField}
              value={form.category}
              onChange={(value) =>
                setForm((previous) => ({ ...previous, category: value as ProductionCategory }))
              }
              options={productionCategories.map((category) => ({
                value: category,
                label: categoryLabel(category),
              }))}
            />
          </div>

          <p className="text-xs text-muted">{ru.programs.priceHint}</p>

          <label className="flex min-h-touch items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={form.isSchool}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, isSchool: event.target.checked }))
              }
              className="h-5 w-5 accent-[var(--amber)]"
            />
            {ru.programs.school}
          </label>

          {sites.length > 0 ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="text-xs uppercase tracking-wider text-muted">{ru.programs.sites}</legend>
              {sites.map((site) => (
                <label key={site.id} className="flex min-h-touch items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={form.siteIds.includes(site.id)}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        siteIds: event.target.checked
                          ? [...previous.siteIds, site.id]
                          : previous.siteIds.filter((value) => value !== site.id),
                      }))
                    }
                    className="h-5 w-5 accent-[var(--amber)]"
                  />
                  <span>{site.title}</span>
                </label>
              ))}
            </fieldset>
          ) : null}

          {formError ? <p className="text-xs text-danger">{formError}</p> : null}

          <Button type="submit" tone="accent" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? ru.common.saving : ru.common.save}
          </Button>
        </form>
      </Card>

      <Card title={ru.programs.title}>
        <AsyncState
          isLoading={excursionsQuery.isLoading}
          isError={excursionsQuery.isError}
          isEmpty={excursions.length === 0}
          emptyTitle={ru.programs.empty}
          emptyDescription={ru.programs.emptyDescription}
          onRetry={() => void excursionsQuery.refetch()}
        >
          <ul className="flex flex-col gap-3">
            {excursions.map((excursion) => (
              <li key={excursion.id} className="rounded-card border border-line p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">{excursion.title}</strong>
                  <Badge tone={excursion.status === ExcursionStatus.Published ? 'success' : 'default'}>
                    {excursionStatusLabel(excursion.status)}
                  </Badge>
                </div>
                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.programs.categoryField} value={categoryLabel(excursion.category)} />
                  <KeyValue label={ru.programs.durationField} value={`${excursion.durationMin} мин`} mono />
                  <KeyValue label={ru.programs.priceField} value={`${(excursion.price / 100).toFixed(2)} ₽`} mono />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => openEdit(excursion.id)}>
                    {ru.common.edit}
                  </Button>
                  {excursion.status !== ExcursionStatus.Archived ? (
                    <Button
                      variant="outline"
                      tone="warning"
                      disabled={archiveMutation.isPending}
                      onClick={() => archiveMutation.mutate(excursion.id)}
                    >
                      {ru.programs.archive}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
      </Card>
    </AppShell>
  );
}
